<template>
  <validation-observer ref="observer">
    <modal-wrapper>
      <s-card-header style="display:block;">
        <h1>{{ title }}</h1>
        <span>{{ selectedProject.name }},{{ selectedTask.name }}</span>
      </s-card-header>
      <s-card-content style="padding-top: 0;">
        <section style="margin: 24px 0; display: flex;justify-content: space-between;">
          <validation-provider v-slot="{ errors }" name="date" rules="required">
            <b-field :label="!edit ? 'Date' : 'Start day'" :type="{ 'is-danger': errors[0] }" :message="errors[0]">
              <b-datepicker
                v-model="bookDate"
                :max-date="maxDate"
                name="date"
                placeholder="Click to select..."
                :date-formatter="formatDates"
                :first-day-of-week="1"
              >
              </b-datepicker>
            </b-field>
          </validation-provider>
          <validation-provider v-if="!edit" v-slot="{ errors }" name="time" :rules="{ required: true }">
            <b-field label="Time spent" :type="{ 'is-danger': errors[0] }" :message="errors[0]">
              <s-input v-model="timeAddEntry" placeholder="1h 30m"></s-input>
            </b-field>
          </validation-provider>
        </section>

        <section v-if="entryData && edit">
          <h3>Time entries</h3>
          <s-table class="timings">
            <s-table-header>
              <s-table-cell></s-table-cell>

              <s-table-cell></s-table-cell>

              <s-table-cell></s-table-cell>

              <s-table-cell></s-table-cell>

              <s-table-cell></s-table-cell>
            </s-table-header>

            <s-table-row
              v-for="timing in timings.slice()"
              :key="timing.id"
              :class="{ 'bg-timer-running': !timing.end && entryData?.isRunning }"
            >
              <s-table-cell>
                <validation-provider
                  v-slot="{ errors, validate }"
                  :name="`${timing.id}-start`"
                  rules="is-date|required"
                  class="grid-time"
                >
                  <b-field>
                    <b-datepicker
                      v-model="timing.startDate"
                      :max-date="maxDate"
                      icon="calendar-today"
                      editable
                      :date-formatter="formatDates"
                      @blur="updateTime(timing.startDate, timing, validate)"
                    >
                    </b-datepicker>
                  </b-field>

                  <b-field>
                    <b-timepicker
                      v-model="timing.startTime"
                      :time-parser="parseTime"
                      :class="{ 'is-danger': errors[0] }"
                      icon="clock"
                      :message="errors[0]"
                      placeholder="Start"
                      :name="`${timing.id}-start`"
                      hour-format="24"
                      editable
                      @blur="updateTime(timing.startTime, timing, validate)"
                    >
                    </b-timepicker>
                  </b-field>
                </validation-provider>
              </s-table-cell>

              <s-table-cell style="position: relative">
                <div v-if="entryData?.isRunning && !timing.end" class="disable-time"></div>

                <validation-provider
                  v-slot="{ errors, validate }"
                  :name="`${timing.id}-end`"
                  rules="is-date"
                  class="grid-time"
                >
                  <b-field>
                    <b-datepicker
                      v-model="timing.endDate"
                      :max-date="maxDate"
                      icon="calendar-today"
                      :editable="!entryData?.isRunning"
                      :date-formatter="formatDates"
                      @blur="updateTime(timing.endDate, timing, validate)"
                    >
                    </b-datepicker>
                  </b-field>

                  <b-field>
                    <b-timepicker
                      v-model="timing.endTime"
                      :time-parser="parseTime"
                      :class="{ 'is-danger': errors[0] }"
                      icon="clock"
                      :message="errors[0]"
                      placeholder="End"
                      :name="`${timing.id}-end`"
                      hour-format="24"
                      :editable="!entryData?.isRunning"
                      @blur="updateTime(timing.endTime, timing, validate)"
                    >
                    </b-timepicker>
                  </b-field>
                </validation-provider>
              </s-table-cell>

              <s-table-cell>
              </s-table-cell>

              <s-table-cell>
                <Duration
                  v-if="!!timing.end"
                  :duration="getDuration(timing.totalSeconds)"
                  @update="(duration) => updateTotal(timing, duration)"
                >
                </Duration>

                <div v-else-if="entryData?.isRunning">
                  <s-number>
                    {{ currentTimer | formatActiveTimer }}
                  </s-number>
                </div>
              </s-table-cell>

              <s-table-cell is-pull-right>
                <s-icon-button
                  v-if="!!timing.end"
                  :disabled="timing.loading"
                  is-delete
                  @click="deleteTimeEntry(timing)"
                ></s-icon-button>
                <button v-else :disabled="timing.loading" @click="onPauseTimer" style="border: none; background: none">
                  <img src="~assets/stop-circle.svg" />
                </button>
              </s-table-cell>
            </s-table-row>
          </s-table>
        </section>
        <section v-if="edit && !entryData?.isRunning && !task" class="flex" style="margin: 12px 0; gap: 20px">
          <button class="btn-link flex" @click="onStartTimer" style="gap: 4px">
            <img src="~/assets/play.svg" />
            Start a new timer
          </button>
          <button class="btn-link flex" style="gap: 4px" @click="addTimeEntry">
            <img src="~/assets/plus-square.svg" />
            Add a new time entry
          </button>
        </section>

        <section>
          <validation-provider v-slot="{ errors }" name="comment" vid="comment" :rules="commentValidation" slim>
            <s-field
              label="Comment"
              :type="{ 'is-danger': errors[0] }"
              :message="errors[0]"
              :required="commentRequired"
              immediate
            >
              <s-input v-model="comment" type="textarea" name="comment" placeholder="What did you work on?"></s-input>
            </s-field>
          </validation-provider>
        </section>
      </s-card-content>

      <s-card-footer v-if="entryData" :class="{ 'justify-space-between': !!edit }">
        <s-button v-if="!task" has-i-trash is-large is-danger @click="deleteEntry"> Delete timer </s-button>
        <s-button
          v-if="edit"
          is-large
          is-primary
          has-i-edit
          :is-disabled="commentRequired && !comment"
          @click="finishTimer"
        >
          Submit
        </s-button>

        <s-button v-else is-primary is-large :is-disabled="commentRequired && !comment" @click="finishTimer">
          {{ saveLabel }}
        </s-button>
        <s-button v-if="task" is-large @click="cancel"> Cancel</s-button>
      </s-card-footer>

      <s-card-footer v-else>
        <s-button tag="input" value="Create entry" type="is-primary" @click="createEntry" />
        <s-button tag="input" value="Cancel" type="is-link" @click="cancel" />
      </s-card-footer>
    </modal-wrapper>
  </validation-observer>
</template>

<style lang="scss" scoped>
@import '~assets/scss/imports';

.s-table.timings {
  margin-bottom: 20px;

  .s-table-header,
  .s-table-row {
    grid-template-columns: 2fr 2fr 0.5fr 2fr 1fr;
    grid-gap: 10px;
  }

  .s-table-row {
    background-color: $cloud;

    &.total {
      background-color: $mint;
    }
  }
}

.grid-time {
  display: grid;
  align-items: center;
  grid-template-columns: repeat(2, 1fr);
  grid-gap: 30px;

  .field {
    margin: 0 rem(4px);
  }
}

::v-deep .datepicker,
::v-deep .timepicker,
::v-deep .custom-inline-fields {
  input {
    border: none;
    border-bottom: rem(1px) dashed gray;
    border-radius: 0;
    background: transparent;
  }
}

.justify-space-between {
  justify-content: space-between !important;
}
::v-deep .is-colon {
  margin-top: 7px !important;
}
.bg-timer-running {
  background: #54a25b1f !important;
}
.btn-link {
  color: $primary !important;
}
.disable-time {
  height: 100%;
  width: 100%;
  position: absolute;
  top:0;
  left: 0;
  z-index: 10;
}
</style>

<script>
import createTimeEntryMutation from '~/graphql/mutations/createTimeEntry.graphql'
import updateTimeEntryMutation from '~/graphql/mutations/updateTimeEntry.graphql'
import deleteTimeEntryMutation from '~/graphql/mutations/deleteTimeEntry.graphql'
import deleteEntryMutation from '~/graphql/mutations/deleteEntry.graphql'
import updateEntryMutation from '~/graphql/mutations/updateEntry.graphql'
import createEntryMutation from '~/graphql/mutations/createEntry.graphql'
import startTimerMutation from '~/graphql/mutations/startTimer.graphql'
import projectsQuery from '~/graphql/queries/projects.graphql'
import entriesQuery from '~/graphql/queries/entries.graphql'
import getEntryQuery from '~/graphql/queries/entry.graphql'
import tasksQuery from '~/graphql/queries/tasks.graphql'
import Duration from '~/components/dashboard/Duration'
import moment from 'moment'
import * as _ from 'lodash'
import SNumber from '~/components/utils/SNumber'
import STable from '~/components/utils/STable'
import STableCell from '~/components/utils/STableCell'
import $notify from "@/plugins/helpers";

export default {
  components: {
    STableCell,
    STable,
    SNumber,
    Duration,
    // MinimalTimer,
  },
  timers: {
    tick: {
      time: 1000,
      autostart: false,
      repeat: true,
    },
  },
  filters: {
    formatActiveTimer(value) {
      if (value == null) return ''
      const m = moment.duration(value, 'seconds')
      if (m.asMilliseconds()) {
        return m.format('H[h] m[m] s[s]', { minValue: 1, trim: false, trunc: true })
      }
      return m.format('H[h] m[m] s[s]', { trim: false })
    },
  },
  props: {
    entry: {
      type: Object,
      default: null,
    },
    edit: {
      type: Boolean,
      default: false,
    },
    task: {
      type: Object,
      default: null,
    },
    title: {
      type: String,
      default: null,
    },
    commentRequired: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      flag: false,
      newEntryId: null,
      projectFilter: '',
      segmentFilter: '',
      selectedSegment: '',
      taskFilter: '',
      bookDate: moment().toDate(),
      maxDate: moment().toDate(),
      timeAddEntry: '',
      invalid: true,
      finished: false,
      comment: '',
      selectedProject: this.entry ? this.entry.project : this.task ? this.task.project : '',
      selectedTask: this.entry
        ? this.entry.task
        : this.task
        ? {
            id: this.task.id,
            name: this.task.name,
          }
        : '',
      entryData: JSON.parse(JSON.stringify(this.entry)),
      time: window.time || null,
      timeRegex: /^(?:[1-9]|1[0-9]|2[0-3])h(?: (?:[0-5]?[0-9])m)?$/,
    }
  },
  apollo: {
    projects: {
      query: projectsQuery,
      variables() {
        return {
          organisation: this.$route.params.organisation,
        }
      },
      update(data) {
        return data.projects ? data.projects : []
      },
    },
    tasks: {
      query: tasksQuery,
      variables() {
        return {
          organisationId: this.$route.params.organisation,
          projectId: this.selectedProject ? this.selectedProject.id : this.entry ? this.entry.projectId : null,
        }
      },
      update(data) {
        return data.tasks.items ? data.tasks.items : []
      },
      skip: true,
    },
    entries: {
      query: entriesQuery,
      variables() {
        return {
          organisationId: this.$route.params.organisation,
          finished: false,
          taskId: this.entry ? this.entry.taskId : null,
          onlyMyEntries: true,
        }
      },
      skip: true,
    },
  },
  computed: {
    selectedDate() {
      return this.$store.getters['timer/getSelectedDate']
    },
    projectGroupByCustomer() {
      return _.values(
        _.mapValues(
          _.groupBy(this.projects, (e) => e.customerId),
          (item) => {
            return {
              customerName: item[0].customer.name,
              list: item,
            }
          }
        )
      )
    },
    taskGroupBySegment() {
      return _.values(
        _.mapValues(
          _.groupBy(this.tasks, (e) => e.segmentId),
          (item) => {
            return {
              segmentName: item[0].segment.name,
              list: item,
            }
          }
        )
      )
    },
    timings() {
      this.flag
      if (this.entryData) {
        let temp = this.updateTimings(this.entryData.timings)
        if (this.selectedDate && temp[0]) {
          temp[0].startDate = this.selectedDate
          temp[0].endDate = this.selectedDate
        }
        return temp
      }
      return []
    },
    commentValidation() {
      if (this.segment) {
        if (this.segment.commentsRequired) {
          return 'required'
        }
      }
      return ''
    },
    currentTimer() {
      if (this.time) {
        return this.time
      }
      return 0
    },
    isTimerRunning() {
      return this.timings.some((p) => !p.end)
    },
    saveLabel() {
      return this.task ? 'Submit' : 'Save and Finish'
    },
    runningId() {
      return this.$store.getters['timer/getRunningId']
    },
  },
  watch: {
    selectedProject() {
      this.selectedTask = null
      this.$apollo.queries.tasks.skip = false
      this.$apollo.queries.tasks.refetch()
    },
    entryData(entry) {
      if (entry && entry.isRunning) {
        this.$timer.start('tick')
      } else {
        this.$timer.stop('tick')
      }
    },
    entries() {
      this.time = this.entries.length ? 0 : this.time
      this.entryData = this.entries.length ? this.entries[0] : this.entryData
    },
    runningId() {
      this.time = 0
      this.reloadEntry()
    },
    bookDate(newVal) {
      this.flag = !this.flag
      if (this.timings.length && this.task) {
        this.timings[0].startDate = newVal
        this.timings[0].endDate = newVal
      }
    },
  },
  async mounted() {
    if (this.selectedDate) this.bookDate = this.selectedDate
    this.setFields()
    if (this.task) {
      this.createEntry()
    }
    // Handling timer when entry is running
    if (this.entryData) {
      if (this.entryData.isRunning && this.edit) {
        let start = this.entryData.timings[this.entryData.timings.length - 1].start
        let startInSeconds = moment(start).diff(moment().startOf('day'), 'seconds')
        let now = moment().diff(moment().startOf('day'), 'seconds')
        this.time = now - startInSeconds
        this.$timer.start('tick')
      }
    }
  },
  methods: {
    async onStartTimer() {
      await this.$store.dispatch('timer/startTimer', this.entryData.task.id)
      await this.$store.dispatch('timer/getMyEntries', this.$route.params.organisation)
      await this.reloadEntry()
    },
    async onPauseTimer() {
      await this.$store.dispatch('timer/pauseTimer', { stop: false, comment: this.comment })
      await this.$store.dispatch('timer/getMyEntries', this.$route.params.organisation)
      await this.reloadEntry()
    },
    parseTime(time) {
      if (typeof time === 'string' && time.match('^([01]?[0-9]|2[0-3])[0-5][0-9]$')) {
        return moment(time, 'HHmm').toDate()
      } else if (typeof time === 'string' && time.match('^([01]?[0-9]|2[0-3]):[0-5][0-9]$')) {
        return moment(time, 'HH:mm').toDate()
      } else if (typeof time === 'string') {
        return moment().toDate()
      }
      return moment(time).toISOString(true)
    },
    formatDates(date) {
      return `${moment(date).format('DD.MM.YYYY')}`
    },
    updateTimer() {
      this.$apollo.queries.entries.skip = false
      this.$apollo.queries.entries.refetch()
      this.$store.dispatch('timer/getMyEntries', this.$route.params.organisation)
    },
    async pauseTimer() {
      try {
        await this.$apollo.mutate({
          mutation: updateEntryMutation,
          variables: {
            id: this.entry.id,
            comment: this.comment,
            bookDate: moment(this.bookDate).format('YYYY-MM-DD'),
            taskId: this.entry.taskId,
            finished: false,
          },
        })
      } catch (e) {
        if (e.graphQLErrors && e.graphQLErrors[0]?.fieldErrors) {
          this.$refs.observer.setErrors(e.graphQLErrors[0].fieldErrors)
        }
      }
    },
    async startTimer() {
      if (!this.isTimerRunning) {
        try {
          await this.$apollo.mutate({ mutation: startTimerMutation, variables: { taskId: this.entry.taskId } })
          this.reloadEntry()
        } catch (e) {
          if (e.graphQLErrors && e.graphQLErrors[0]?.fieldErrors) {
            this.$refs.observer.setErrors(e.graphQLErrors[0].fieldErrors)
          }
        }
      }
    },
    getTotalSeconds() {
      if (this.timings.length) {
        let totalSeconds
        if (this.timings.length > 1) {
          totalSeconds = this.timings.map((e) => e.totalSeconds).reduce((a, b) => a + b)
        } else {
          totalSeconds = this.timings[0].totalSeconds
        }
        return totalSeconds
      }
      return 0
    },
    tick() {
      this.time = moment.duration(this.time, 'seconds').add(moment.duration(1, 's'))
    },
    getDuration(seconds) {
      return moment.duration(seconds, 'seconds')
    },
    setFields() {
      if (this.entryData) {
        this.bookDate = moment(this.entryData.bookDate).toDate()
        this.comment = this.entryData.comment
        this.selectedSegment = this.entryData.segment
        this.taskFilter = this.entryData.task.name
        this.segmentFilter = this.entryData.segment.name
        this.projectFilter = this.entryData.project.name
        this.finished = this.entryData.finished
      } else {
        // When there is not entry to begin with we set the flag to finish. in this case the user wants to add some
        // time not start a timer
        this.finished = true
      }
    },
    format(time) {
      if (time) {
        return moment(time).format('HH:mm')
      }
      return ''
    },
    updateTimings(timings) {
      const data = JSON.parse(JSON.stringify(timings))
      data.map((p) => {
        p.startDate = p.start ? moment(p.start.split('T')[0]).toDate() : null
        p.startTime = p.start ? moment(p.start).toDate() : null
        p.endDate = p.end ? moment(p.end.split('T')[0]).toDate() : null
        p.endTime = p.end ? moment(p.end).toDate() : null
        p.loading = false
      })
      return data
    },
    async updateTime(time, timeEntry, validate) {
      this.flag = !this.flag
      timeEntry.startDate = time
      timeEntry.endDate = time
      const res = await validate(time)
      if (res.valid && this.validateDateTimePicker(timeEntry)) {
        const data = {}

        if (timeEntry.startTime && timeEntry.startDate) {
          data['start'] = moment(
            moment(timeEntry.startDate).format('YYYY-MM-DD') + ' ' + moment(timeEntry.startTime).format('HH:mm')
          ).format()
        } else {
          data['start'] = timeEntry.startTime
            ? moment(timeEntry.startTime, 'HH:mm').format()
            : timeEntry.startDate
            ? moment(timeEntry.startDate, 'HH:mm').format()
            : null
        }

        if (timeEntry.endTime && timeEntry.endDate) {
          data['end'] = moment(
            moment(timeEntry.endDate).format('YYYY-MM-DD') + ' ' + moment(timeEntry.endTime).format('HH:mm')
          ).format()
        } else {
          data['end'] = timeEntry.endTime
            ? moment(timeEntry.endTime, 'HH:mm').format()
            : timeEntry.endDate
            ? moment(timeEntry.endDate, 'HH:mm').format()
            : null
        }
        await this.updateTimeEntry(timeEntry, data)
      }
    },
    async updateTimeAddEntry(time, timeEntry) {
      this.flag = !this.flag
      if (this.validateDateTimePicker(timeEntry)) {
        await this.updateTimeEntry(timeEntry, { end: time, start: timeEntry.start })
      }
    },
    parseTimeString(timeStr) {
      timeStr = timeStr.trim().toLowerCase().replace(/\s+/g, '');

      let hours = 0;
      let minutes = 0;

      // Match formats like "1h", "0.3h", "1.75h"
      const floatHourMatch = timeStr.match(/^([\d.]+)h$/);
      if (floatHourMatch) {
        const totalHours = parseFloat(floatHourMatch[1]);
        hours = Math.floor(totalHours);
        minutes = Math.round((totalHours - hours) * 60);
        return { hours, minutes };
      }

      // Match formats like "45m", "45min"
      const onlyMinutesMatch = timeStr.match(/^(\d{1,3})(m|min)$/);
      if (onlyMinutesMatch) {
        minutes = parseInt(onlyMinutesMatch[1]);
        return { hours, minutes };
      }

      // Match HH:MM or H:MM
      const colonTimeMatch = timeStr.match(/^(\d{1,2}):(\d{1,2})$/);
      if (colonTimeMatch) {
        hours = parseInt(colonTimeMatch[1]);
        minutes = parseInt(colonTimeMatch[2]);
        return { hours, minutes };
      }

      // Match "1h:23min" or "1h:23m"
      const hColonMMatch = timeStr.match(/^(\d{1,2})h[:\s]?(\d{1,2})(m|min)?$/);
      if (hColonMMatch) {
        hours = parseInt(hColonMMatch[1]);
        minutes = parseInt(hColonMMatch[2]);
        return { hours, minutes };
      }

      // Match "1h23m", "1hr24min", "1hr22min"
      const compactHrMinMatch = timeStr.match(/^(\d{1,2})(h|hr)(\d{1,2})(m|min)$/);
      if (compactHrMinMatch) {
        hours = parseInt(compactHrMinMatch[1]);
        minutes = parseInt(compactHrMinMatch[3]);
        return { hours, minutes };
      }

      // Match "1h 23m", "3h5m", "1hr 20m", "1hr 14min", etc.
      const flexibleHrMinMatch = timeStr.match(/^(\d+(?:\.\d+)?)(h|hr)(\d{1,2})?(m|min)?$/);
      if (flexibleHrMinMatch) {
        const h = parseFloat(flexibleHrMinMatch[1]);
        const m = flexibleHrMinMatch[3] ? parseInt(flexibleHrMinMatch[3]) : 0;
        hours = Math.floor(h);
        minutes = Math.round((h - hours) * 60) + m;
        return { hours, minutes };
      }

      // Match "1 h 43 m", with optional spaces (already removed at start)
      const spacedHrMinMatch = timeStr.match(/^(\d{1,2})h(\d{1,2})m$/);
      if (spacedHrMinMatch) {
        hours = parseInt(spacedHrMinMatch[1]);
        minutes = parseInt(spacedHrMinMatch[2]);
        return { hours, minutes };
      }

      // If only a decimal hour with no suffix, e.g. "1.5"
      const floatHOnlyMatch = timeStr.match(/^([\d.]+)$/);
      if (floatHOnlyMatch) {
        const totalHours = parseFloat(floatHOnlyMatch[1]);
        hours = Math.floor(totalHours);
        minutes = Math.round((totalHours - hours) * 60);
        return { hours, minutes };
      }

      $notify(
        `Unrecognized format: "${timeStr}"`,
        'is-danger',
        true,
        'Please add the time entry like this: 1h 5m'
      );
      throw new Error(`Unrecognized format: "${timeStr}"`);
    },
    addDurationToDateTime(dateTimeString, durationString) {
      const {hours, minutes} = this.parseTimeString(durationString)
      if (!hours && !minutes) {
        return
      }
      const dateTime = moment(dateTimeString)
      return moment(dateTime.add(hours, 'hours').add(minutes, 'minutes')).format('YYYY-MM-DDTHH:mm:ssZ')
    },
    validateDateTimePicker(timeEntry) {
      let combineFirstDate = timeEntry.startTime ? moment(timeEntry.startTime) : moment(timeEntry.startDate)
      if (timeEntry.startTime && timeEntry.startDate) {
        combineFirstDate = moment(
          moment(timeEntry.startDate).format('YYYY-MM-DD') + ' ' + moment(timeEntry.startTime).format('HH:mm')
        ).toDate()
      }

      let combineEndDate = timeEntry.endTime ? moment(timeEntry.endTime) : moment(timeEntry.endDate)
      if (timeEntry.endTime && timeEntry.endDate) {
        combineEndDate = moment(
          moment(timeEntry.endDate).format('YYYY-MM-DD') + ' ' + moment(timeEntry.endTime).format('HH:mm')
        ).toDate()
      }

      const duration = moment(combineEndDate).diff(moment(combineFirstDate), 'hours')

      if (moment(combineEndDate).isBefore(moment(combineFirstDate))) {
        this.$notify('End time should be more than start time', 'is-danger', true)
        timeEntry.totalSeconds = 0
        return false
      }
      if (duration >= 24) {
        this.$notify('There are only 24hrs in a day! ⏳', 'is-danger', true)
        timeEntry.totalSeconds = 0
        return false
      }
      return true
    },
    updateTotal: _.debounce(function (timeEntry, duration) {
      const start = timeEntry.start
      const data = {}
      if (duration) {
        data['end'] = moment(start).add(moment.duration(duration).asSeconds(), 'seconds').format()
      } else {
        data['end'] = null
      }

      this.updateTimeEntry(timeEntry, data)
    }, 600),
    async updateTimeEntry(timeEntry, data) {
      try {
        const response = await this.$apollo.mutate({
          mutation: updateTimeEntryMutation,
          variables: {
            id: timeEntry.id,
            start: timeEntry.start,
            ...data,
          },
        })
        this.entryData.timings = this.timings.map((te) => {
          if (te.id === timeEntry.id) {
            let data = response.data.updateTimeEntry
            data.startDate = data.start ? moment(data.start.split('T')[0]).toDate() : null
            data.startTime = data.start ? moment(data.start).toDate() : null
            data.endDate = data.end ? moment(data.end.split('T')[0]).toDate() : null
            data.endTime = data.end ? moment(data.end).toDate() : null
            return data
          }
          return te
        })
      } catch (e) {
        if (e.graphQLErrors.length && e.graphQLErrors[0]?.fieldErrors) {
          console.log(e.graphQLErrors[0].fieldErrors)
        }
      }
    },
    async reloadEntry() {
      const response = await this.$apollo.query({
        query: getEntryQuery,
        variables: {
          id: this.entryData.id,
        },
        fetchPolicy: 'no-cache',
      })
      this.entryData.timings = this.updateTimings(response.data.entry.timings)
      this.entryData.isRunning = response.data.entry.isRunning
    },
    async deleteTimeEntry(timing) {
      timing.loading = true
      this.$forceUpdate()
      this.$buefy.dialog.confirm({
        title: 'Delete time interval',
        message: 'Are you sure? This action cannot be undone.',
        confirmText: 'Yes, delete interval',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          try {
            await this.$apollo.mutate({
              mutation: deleteTimeEntryMutation,
              variables: {
                id: timing.id,
              },
            })
            await this.reloadEntry()
          } catch (e) {
            timing.loading = false
            this.$forceUpdate()
            console.log(e)
          }
        },
        onCancel: () => {
          timing.loading = false
          this.$forceUpdate()
        },
      })
    },
    async deleteEntry() {
      this.$buefy.dialog.confirm({
        title: 'Remove timer',
        message: 'Are you sure? This action cannot be undone.',
        confirmText: 'Yes, delete timer!',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          await this.$apollo.mutate({
            mutation: deleteEntryMutation,
            variables: {
              id: this.entryData.id,
            },
          })
          this.updateTimer()
          this.$emit('close')
        },
      })
    },
    async addTimeEntry() {
      try {
        const response = await this.$apollo.mutate({
          mutation: createTimeEntryMutation,
          variables: {
            entryId: this.entryData.id,
            start: moment().format(),
            end: moment().add(1, 'second').format(),
          },
        })
        this.entryData.timings.push(response.data.createTimeEntry)
        requestAnimationFrame(() => {
          this.$refs.observer.reset()
        })
      } catch (e) {
        if (e.graphQLErrors && e.graphQLErrors[0]?.fieldErrors) {
          console.log(e.graphQLErrors[0].fieldErrors)
        }
      }
    },
    finishTimer() {
      this.finished = true
      this.save()
    },
    getSecondsDifference(t1, t2) {
      const date1 = new Date(t1);
      const date2 = new Date(t2);
      const diffMs = Math.abs(date2 - date1); // milliseconds
      return Math.floor(diffMs / 1000); // convert to seconds
    },
    async save() {
      const isValid = await this.$refs.observer.validate()
      if (isValid) {
        if (this.getDuration(this.getTotalSeconds()).asHours() >= 24) {
          this.$notify('There are only 24hrs in a day! ⏳', 'is-danger', true)
        } else {
          let taskId
          if (this.selectedTask) {
            taskId = this.selectedTask.id
          } else {
            taskId = this.task.id
          }
          try {
            if (!this.edit) {
              const newEndDate = this.addDurationToDateTime(this.entryData.timings[0].start, this.timeAddEntry)
              if (this.getSecondsDifference(newEndDate, this.entryData.timings[0].start) < 59) {
                return;
              }
              await this.updateTimeAddEntry(newEndDate, this.entryData.timings[0])
            }
            await this.$apollo.mutate({
              mutation: updateEntryMutation,
              variables: {
                id: this.entryData.id,
                comment: this.comment,
                bookDate: moment(this.bookDate).format('YYYY-MM-DD'),
                taskId: taskId,
                finished: this.finished,
              },
            })
            this.updateTimer()
            this.$emit('close')
          } catch (e) {
            console.log('error', e)
            if (e.graphQLErrors && e.graphQLErrors[0]?.fieldErrors) {
              this.$refs.observerComment.setErrors(e.graphQLErrors[0].fieldErrors)
            }
          }
        }
      } else {
        this.$notify('Form is invalid. Please fix validation errors.', 'is-danger', true)
      }
    },
    async createEntry() {
      let taskId
      if (this.selectedTask) {
        taskId = this.selectedTask.id
      } else {
        taskId = this.task.id
      }
      try {
        const response = await this.$apollo.mutate({
          mutation: createEntryMutation,
          variables: {
            bookDate: moment(this.bookDate).format('YYYY-MM-DD'),
            taskId: taskId,
            finished: false,
          },
        })
        this.entryData = response.data.createEntry
        this.addTimeEntry()
      } catch (e) {
        if (e.graphQLErrors && e.graphQLErrors[0]?.fieldErrors) {
          this.$refs.observer.setErrors(e.graphQLErrors[0].fieldErrors)
        }
      }
      // }
    },
    async cancel() {
      if (this.entryData && this.getTotalSeconds() <= 59 && !this.edit && this.task) {
        try {
          await this.$apollo.mutate({
            mutation: deleteEntryMutation,
            variables: {
              id: this.entryData.id,
            },
          })
          this.updateTimer()
          this.$emit('close')
        } catch (e) {
          console.log(e)
        }
      } else {
        this.$emit('close')
      }
    },
  },
}
</script>
